import { baseApi } from './api';
import {
    Ticket,
    TicketResultType,
    TicketPageResultType,
    TicketDirectory,
} from 'types/User/firebaseTypes/ticket';

export const issuesApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        /** Get a page of issue tickets  **/
        getIssueTicketsPage: builder.query<
            TicketPageResultType,
            {
                supplierId: string | null;
                ticketDirectory: TicketDirectory;
                startAt?: string;
                limit?: number;
            }
        >({
            query: ({ supplierId, startAt, limit, ticketDirectory }) => {
                if (supplierId === '0') {
                    throw new Error(
                        'Should not have issues under supplierId 0.'
                    );
                }

                if (!supplierId) throw new Error('supplierId is required.');
                if (!ticketDirectory) {
                    throw new Error('ticketDirectory is required.');
                }

                return {
                    method: 'GET',
                    url: `tickets/${supplierId}/issue/${ticketDirectory}`,
                    params: {
                        paginate: true,
                        startAt,
                        limit,
                    },
                };
            },
            transformResponse: (res: any) => {
                return { ...res };
            },
            transformErrorResponse: (res: any) => {
                return { ...res };
            },
        }),
        /** Get all active issue tickets for a given device. */
        getIssueTicketsByDeviceId: builder.query<
            TicketResultType,
            { supplierId: string | null; deviceId: string | null }
        >({
            query: ({ supplierId, deviceId }) => ({
                method: 'GET',
                url: `tickets/${supplierId}/issue/active/${deviceId}`,
            }),
            transformErrorResponse: (res: any) => {
                return { ...res };
            },
            transformResponse: (res: any) => {
                return { ...res };
            },
        }),
        /** Get all resolved issue tickets for a given device. */
        getResolvedIssueTicketsByDeviceId: builder.query<
            TicketResultType,
            { supplierId: string | null; deviceId: string | null }
        >({
            query: ({ supplierId, deviceId }) => ({
                method: 'GET',
                url: `tickets/${supplierId}/issue/resolved/${deviceId}`,
            }),
            transformErrorResponse: (res: any) => {
                return { ...res };
            },
            transformResponse: (res: any) => {
                return { ...res };
            },
        }),
        getActiveIssueTicketsBySupplierId: builder.query<
            TicketPageResultType,
            { supplierId: string }
        >({
            query: ({ supplierId }) => ({
                method: 'GET',
                url: `tickets/${supplierId}/issue/active`,
            }),
            transformResponse: (res: any) => {
                return { ...res };
            },
        }),
        /** Create a new active issue ticket. */
        createIssueTicket: builder.query<
            Ticket,
            {
                supplierId: string | null;
                deviceId: string;
                ticket: Partial<Ticket>;
            }
        >({
            query: ({ supplierId, deviceId, ticket }) => ({
                method: 'POST',
                url: `tickets/${supplierId}/issue/active/${deviceId}`,
                body: ticket,
            }),
        }),
        /** Update an active issue ticket. */
        updateIssueTicket: builder.mutation<
            Ticket,
            {
                supplierId: string;
                deviceId: string;
                newTicket: Ticket;
            }
        >({
            query: ({ newTicket, supplierId, deviceId }) => {
                if (!supplierId) {
                    throw new Error('supplierId is required.');
                }
                if (!deviceId) {
                    throw new Error('deviceId is required.');
                }
                return {
                    method: 'PUT',
                    url: `tickets/${supplierId}/issue/active/${deviceId}`,
                    body: newTicket,
                };
            },
            transformResponse: (response: any) => {
                return { ...response };
            },
        }),
        /** Resolve an active issue ticket. */
        resolveIssueTicket: builder.mutation<
            Ticket,
            {
                supplierId: string;
                deviceId: string;
                newTicket: Ticket;
            }
        >({
            query: ({ newTicket, supplierId, deviceId }) => {
                if (!supplierId) {
                    throw new Error('supplierId is required.');
                }
                if (!deviceId) {
                    throw new Error('deviceId is required.');
                }
                return {
                    method: 'POST',
                    url: `tickets/${supplierId}/issue/resolved/${deviceId}`,
                    body: newTicket,
                };
            },
            transformResponse: (response: any) => {
                return { ...response };
            },
        }),
        /** Update an already resolved issue ticket. */
        updateResolvedIssueTicket: builder.mutation<
            Ticket,
            {
                supplierId: string;
                deviceId: string;
                newTicket: Ticket;
            }
        >({
            query: ({ newTicket, supplierId, deviceId }) => {
                if (!supplierId) {
                    throw new Error('supplierId is required.');
                }
                if (!deviceId) {
                    throw new Error('deviceId is required.');
                }
                return {
                    method: 'PUT',
                    url: `tickets/${supplierId}/issue/resolved/${deviceId}`,
                    body: newTicket,
                };
            },
            transformResponse: (response: any) => {
                return { ...response };
            },
        }),
    }),
});

export const {
    useLazyCreateIssueTicketQuery,
    useLazyGetIssueTicketsPageQuery,
    useLazyGetResolvedIssueTicketsByDeviceIdQuery,
    useUpdateIssueTicketMutation,
    useLazyGetIssueTicketsByDeviceIdQuery,
    useResolveIssueTicketMutation,
    useUpdateResolvedIssueTicketMutation,
    useLazyGetActiveIssueTicketsBySupplierIdQuery,
} = issuesApi;
